/**
 * ShapeViewer - LitElement component for displaying shape compositions.
 */

import shapeviewerStyles from './ShapeViewer.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type {
  ShapeComposition,
  RenderOptions,
  RenderResult,
  ComputedRegion,
} from '../types';
import { ShapeRenderer, createRenderer } from '../renderer';
import { AssetManager } from '../models/AssetManager';

/**
 * Events emitted by the ShapeViewer component.
 */
export interface ShapeViewerEventMap {
  'region-click': CustomEvent<{ region: ComputedRegion; event: MouseEvent }>;
  'region-hover': CustomEvent<{
    region: ComputedRegion | null;
    event: MouseEvent;
  }>;
  'render-complete': CustomEvent<{ result: RenderResult }>;
}

/**
 * A component for viewing/previewing shape compositions.
 *
 * @element ptc-viewer
 *
 * @fires region-click - When a region is clicked
 * @fires region-hover - When hovering over a region
 * @fires render-complete - When rendering completes
 */
@customElement('ptc-viewer')
export class ShapeViewer extends LitElement {
  static override styles = unsafeCSS(shapeviewerStyles);

  /**
   * The shape composition to render.
   */
  @property({ type: Object })
  composition: ShapeComposition | null = null;

  /**
   * Render options.
   */
  @property({ type: Object })
  options: RenderOptions = {};

  /**
   * Whether to enable interactive region selection.
   */
  @property({ type: Boolean })
  interactive = false;

  /**
   * Asset manager for loading SVG assets.
   */
  @property({ attribute: false })
  assetManager: AssetManager | null = null;

  @state()
  private renderResult: RenderResult | null = null;

  @state()
  private loading = false;

  @state()
  private error: string | null = null;

  private renderer: ShapeRenderer | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.renderer = createRenderer(this.options);
  }

  override willUpdate(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('options')) {
      this.renderer = createRenderer(this.options);
    }

    if (
      changedProperties.has('assetManager') &&
      this.assetManager &&
      this.renderer
    ) {
      this.renderer.setAssetManager(this.assetManager);
    }

    if (
      changedProperties.has('composition') ||
      changedProperties.has('options') ||
      changedProperties.has('assetManager')
    ) {
      void this.doRender();
    }
  }

  /**
   * Perform the render operation.
   */
  private async doRender(): Promise<void> {
    if (!this.composition || !this.renderer) {
      this.renderResult = null;
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      if (this.assetManager) {
        this.renderer.setAssetManager(this.assetManager);
      }

      this.renderResult = await this.renderer.render(this.composition);

      this.dispatchEvent(
        new CustomEvent('render-complete', {
          detail: { result: this.renderResult },
          bubbles: true,
          composed: true,
        })
      );
    } catch (e) {
      this.error = e instanceof Error ? e.message : 'Render failed';
      this.renderResult = null;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Handle click on the SVG.
   */
  private handleClick(event: MouseEvent): void {
    if (!this.interactive || !this.renderResult) return;

    const target = event.target as Element;
    const regionElement = target.closest('[data-region-id]');

    if (regionElement) {
      const regionId = regionElement.getAttribute('data-region-id');
      if (regionId && this.renderer) {
        const computed = this.renderer
          .getLayoutEngine()
          .findRegionById(this.renderResult.computedRoot, regionId);

        if (computed) {
          this.dispatchEvent(
            new CustomEvent('region-click', {
              detail: { region: computed, event },
              bubbles: true,
              composed: true,
            })
          );
        }
      }
    }
  }

  /**
   * Handle mouse move on the SVG.
   */
  private handleMouseMove(event: MouseEvent): void {
    if (!this.interactive || !this.renderResult) return;

    const target = event.target as Element;
    const regionElement = target.closest('[data-region-id]');

    if (regionElement) {
      const regionId = regionElement.getAttribute('data-region-id');
      if (regionId && this.renderer) {
        const computed = this.renderer
          .getLayoutEngine()
          .findRegionById(this.renderResult.computedRoot, regionId);

        this.dispatchEvent(
          new CustomEvent('region-hover', {
            detail: { region: computed ?? null, event },
            bubbles: true,
            composed: true,
          })
        );
      }
    } else {
      this.dispatchEvent(
        new CustomEvent('region-hover', {
          detail: { region: null, event },
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  /**
   * Get the current render result.
   */
  getRenderResult(): RenderResult | null {
    return this.renderResult;
  }

  /**
   * Force a re-render.
   */
  async refresh(): Promise<void> {
    await this.doRender();
  }

  override render() {
    if (this.loading) {
      return html`
        <div class="viewer-container">
          <div class="loading">Loading...</div>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="viewer-container">
          <div class="error">${this.error}</div>
        </div>
      `;
    }

    if (!this.renderResult) {
      return html`
        <div class="viewer-container">
          <div class="empty">No composition to display</div>
        </div>
      `;
    }

    return html`
      <div class="viewer-container">
        <div class="viewer-content">
          <div
            class="viewer-svg"
            @click=${this.handleClick}
            @mousemove=${this.handleMouseMove}
          >
            ${unsafeHTML(this.renderResult.svg)}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ptc-viewer': ShapeViewer;
  }
}
